import { useUpdated } from "@common/lib/hooks";
import { Common } from "@common/types";
import React from "react";

export type InfiniteScrollProps = Common.WithChildren & {
  className?: string;
  element?: string;
  hasMore?: boolean;
  isStartPosition?: boolean;
  isLoading?: boolean;
  loader?: any;
  loadMore: () => Promise<void> | void;
  height?: number | null;
  onScroll?: () => void;
  tabIndex?: number;
};
const InfiniteScroll = React.forwardRef((props: InfiniteScrollProps, ref: React.RefObject<any>) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [shouldLoad, setShouldLoad] = React.useState(!!props.hasMore);
  const [height, setHeight] = React.useState<number | null | undefined>(props.height);

  ref = ref ?? React.useRef(null);
  const Tag = (props.element || ("div" as unknown)) as React.ComponentType<React.HTMLProps<any>>;

  useUpdated(() => setShouldLoad(!!props.hasMore), [props.hasMore]);
  useUpdated(() => setHeight(props.height), [props.height]);

  React.useEffect(() => {
    if (props.isStartPosition) {
      const el = ref.current! as HTMLElement;
      setHeight(el.offsetHeight);
    }
  }, [props.isStartPosition]);

  React.useEffect(() => {
    const el = ref.current! as HTMLElement;
    const onScroll = (e) => {
      props.onScroll && props.onScroll();

      if (Math.abs(el.scrollHeight - (el.offsetHeight + el.scrollTop)) < 30) setShouldLoad(true);
    };

    if (!isLoading && !props.isLoading) {
      el.addEventListener("scroll", onScroll);
    }

    return () => {
      if (!isLoading && !props.isLoading) {
        el.removeEventListener("scroll", onScroll);
      }
    };
  }, [setShouldLoad, isLoading, props.isLoading, props.onScroll]);

  React.useEffect(() => {
    const load = async () => {
      setShouldLoad(false);
      setIsLoading(true);
      try {
        await props.loadMore();
      } finally {
        setIsLoading(false);
      }
    };

    if (props.hasMore && shouldLoad && !props.isLoading && !isLoading) {
      load();
    }
  }, [props.hasMore, props.loadMore, props.isLoading, shouldLoad, isLoading]);

  return (
    <>
      <Tag
        ref={ref}
        className={props.className}
        tabIndex={props.tabIndex}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: height ?? undefined,
          overflow: "hidden auto"
        }}
      >
        {props.children}
        {(isLoading || props.isLoading) && props.loader}
      </Tag>
    </>
  );
});
export default InfiniteScroll;
